import React from 'react';
import clsx from 'clsx';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Box, Tooltip, Typography } from '@material-ui/core';

const INITIAL_TAB_INDEX = 0;
const DISABLED_TAB_INDEX = -1;

const ControlledSwitchButton = ({
  checked = false,
  onChange,
  leftText = 'Sí',
  rightText = 'No',
  disabled = false,
  tooltipText = '',
  leftIcon,
  rightIcon,
  className,
  ...rest
}) => {
  const classes = useStyles();

  const options = [
    {
      value: false,
      text: leftText,
      icon: leftIcon,
      active: !checked
    },
    {
      value: true,
      text: rightText,
      icon: rightIcon,
      active: checked
    }
  ];

  let selectedTabIndex = INITIAL_TAB_INDEX;

  if (disabled) {
    selectedTabIndex = DISABLED_TAB_INDEX;
  }

  return (
    <Tooltip
      arrow
      placement="left"
      classes={{ tooltip: classes.tooltip }}
      title={tooltipText}
    >
      <Box
        className={clsx(
          classes.container,
          disabled && classes.disabled,
          className
        )}
        {...rest}
      >
        {options.map(({ value, text, icon, active }) => (
          <Box
            key={String(value)}
            className={clsx(classes.optionContent, {
              [classes.active]: active,
              [classes.inactive]: !active
            })}
            role="button"
            tabIndex={selectedTabIndex}
            onClick={() => !disabled && onChange && onChange(value)}
            onKeyDown={e => {
              if (disabled) {
                return;
              }
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange && onChange(value);
              }
            }}
          >
            {icon && (
              <Box
                className={clsx(classes.icon, {
                  [classes.activeIcon]: active,
                  [classes.inactiveIcon]: !active
                })}
              >
                {icon}
              </Box>
            )}

            <Typography
              className={clsx(classes.optionText, {
                [classes.activeText]: active,
                [classes.inactiveText]: !active
              })}
            >
              {text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Tooltip>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    background: '#eef1f7',
    border: '1px solid #dfe4ee',
    borderRadius: 13,
    padding: 4,
    gap: 4
  },
  optionContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    minWidth: 124,
    padding: '9px 16px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 700,
    transition: 'background-color .2s ease, color .2s ease'
  },
  active: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff'
  },
  inactive: {
    color: '#7d8aa6'
  },
  optionText: {
    fontSize: 14,
    fontWeight: 700
  },
  activeText: {
    color: 'inherit'
  },
  inactiveText: {
    color: 'inherit'
  },
  icon: {
    display: 'flex',
    alignItems: 'center'
  },
  activeIcon: {
    '& svg': {
      fill: 'currentColor',
      color: 'currentColor'
    }
  },
  inactiveIcon: {
    '& svg': {
      fill: 'currentColor',
      color: 'currentColor'
    }
  },
  disabled: {
    opacity: 0.6,
    pointerEvents: 'none'
  },
  tooltip: {
    margin: theme.spacing(1.5, 0)
  }
}));

export default ControlledSwitchButton;
